import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import EmptyState from '@/components/base/EmptyState';
import SectionHeading from '@/components/base/SectionHeading';
import WorkCard from '@/components/base/WorkCard';
import DetailHero from '@/pages/detail/components/DetailHero';
import StorySection from '@/pages/detail/components/StorySection';
import ShareModal from '@/pages/detail/components/ShareModal';
import { typeMeta } from '@/mocks/movies';
import { getRelatedWorks, getWorkById, readFavorites, toggleFavorite } from '@/utils/content';

export default function DetailPage() {
  const { id = '' } = useParams();
  const work = getWorkById(id);
  const [favorites, setFavorites] = useState<string[]>([]);
  const [showShare, setShowShare] = useState(false);
  const [toast, setToast] = useState('');

  useEffect(() => {
    setFavorites(readFavorites());
    setShowShare(false);
    setToast('');
  }, [id]);

  useEffect(() => {
    if (work) {
      document.title = `${work.typeLabel}《${work.title}》剧情介绍 - 长日光阴`;
      const desc = document.querySelector('meta[name="description"]');
      if (desc) desc.setAttribute('content', `${work.title}：${work.synopsis}`);
    }
  }, [work]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(''), 2200);
    return () => clearTimeout(timer);
  }, [toast]);

  if (!work) {
    return (
      <div className="mx-auto w-full px-4 py-24 md:px-6">
        <EmptyState
          icon="ri-error-warning-line"
          title="内容加载失败"
          description="没有找到对应的影视作品，可能是链接已失效。你可以回到分类页继续浏览。"
          actionLabel="返回首页"
          onAction={() => {
            window.REACT_APP_NAVIGATE('/');
          }}
        />
      </div>
    );
  }

  const isFavorite = favorites.includes(work.id);
  const related = getRelatedWorks(work, 4);
  const shareUrl = `${window.location.origin}${__BASE_PATH__}/detail/${work.id}`;
  const categoryMeta = typeMeta[work.type];

  const handleToggleFavorite = () => {
    const next = toggleFavorite(work.id);
    setFavorites(next);
    setToast(next.includes(work.id) ? '已加入收藏' : '已取消收藏');
  };

  return (
    <div className="pb-16">
      <DetailHero
        work={work}
        isFavorite={isFavorite}
        onToggleFavorite={handleToggleFavorite}
        onShare={() => setShowShare(true)}
      />

      <div className="mx-auto w-full px-4 py-6 md:px-6">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: categoryMeta?.label ?? '分类', to: `/category/${work.type}` },
            { label: work.title },
          ]}
        />
      </div>

      <div className="mx-auto w-full px-4 md:px-6">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_300px]">
          <StorySection story={work.story} />

          <aside className="flex flex-col gap-5">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h3 className="font-heading text-base font-bold text-foreground-950">作品速览</h3>
              <ul className="mt-4 space-y-3 text-sm">
                <li className="flex items-center justify-between border-b border-background-200 pb-2.5">
                  <span className="text-secondary-500">综合评分</span>
                  <span className="font-bold text-accent-600">{work.rating.toFixed(1)}</span>
                </li>
                <li className="flex items-center justify-between border-b border-background-200 pb-2.5">
                  <span className="text-secondary-500">类型</span>
                  <span className="text-foreground-800">{work.typeLabel}</span>
                </li>
                <li className="flex items-center justify-between border-b border-background-200 pb-2.5">
                  <span className="text-secondary-500">地区</span>
                  <span className="text-foreground-800">{work.region}</span>
                </li>
                <li className="flex items-center justify-between">
                  <span className="text-secondary-500">状态</span>
                  <span className="text-foreground-800">{work.status}</span>
                </li>
              </ul>
              <Link
                to={`/category/${work.type}`}
                className="mt-5 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                浏览更多{work.typeLabel}
                <i className="ri-arrow-right-line" aria-hidden="true" />
              </Link>
            </div>

            <div className="rounded-lg border border-accent-200 bg-accent-50 p-5">
              <h3 className="font-heading text-sm font-bold text-accent-900">温馨提示</h3>
              <p className="mt-2 text-xs leading-relaxed text-accent-900/80">
                本站为影视内容介绍演示站点，仅提供作品信息与剧情解析，不提供在线播放、下载或付费服务。
              </p>
            </div>
          </aside>
        </div>
      </div>

      <div className="mx-auto w-full px-4 pt-14 md:px-6">
        <SectionHeading eyebrow="YOU MAY LIKE" title="相关推荐" description="根据类型与主题为你匹配的相似作品。" />
        {related.length > 0 ? (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-5">
            {related.map((item) => (
              <WorkCard key={item.id} work={item} />
            ))}
          </div>
        ) : (
          <EmptyState icon="ri-film-line" title="暂无相关推荐" description="这部作品暂时还没有匹配到相似内容。" />
        )}
      </div>

      <ShareModal open={showShare} shareUrl={shareUrl} title={work.title} onClose={() => setShowShare(false)} />

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-[80] -translate-x-1/2 rounded-md bg-primary-950 px-5 py-3 text-sm font-medium text-background-50">
          {toast}
        </div>
      )}
    </div>
  );
}